import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { EVENT_TIMEZONES } from '@/lib/eventTimezones';
import TimePicker12h from '@/components/TimePicker12h';
import DatePicker from '@/components/DatePicker';
import { ImageUpload } from '@/components/ImageUpload';
import { X, AlertTriangle, Tag, Loader2, Sparkles, CreditCard } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const EVENT_TYPES = [
  { value: 'casual', label: 'Open Play' },
  { value: 'teaching', label: 'Teaching Session' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'special', label: 'Special Event' },
];
const LOCATION_TYPES = [
  { value: 'public', label: 'Public Venue' },
  { value: 'private', label: 'Private Residence' },
  { value: 'online', label: 'Online / Virtual' },
];
const SKILL_OPTIONS = ['newbie', 'beginner', 'intermediate', 'advanced', 'all'];
const RECURRING_OPTIONS = [
  { value: '', label: 'One-time event' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1.5">{title}</div>
    {children}
  </div>
);

/**
 * Modal for editing every editable field on an event — accessible to the
 * host AND admins. Admins additionally get the price/status controls.
 * Caller receives the updated doc via `onSaved` to patch local UI state.
 */
export default function AdminEventEditModal({ event, onClose, onSaved, mode = 'admin' }) {
  const isAdmin = mode === 'admin';
  const [f, setF] = useState({
    title: event.title || '',
    event_type: event.event_type || 'casual',
    description: event.description || '',
    event_date: event.event_date || '',
    start_time: event.start_time || '',
    end_time: event.end_time || '',
    timezone: event.timezone || 'ET',
    is_recurring: !!event.is_recurring,
    recurring_frequency: event.recurring_frequency || '',
    venue_name: event.venue_name || '',
    street_address: event.street_address || '',
    city: event.city || '',
    state: event.state || '',
    zip_code: event.zip_code || '',
    location_type: event.location_type || 'public',
    is_destination_event: !!event.is_destination_event,
    destination_label: event.destination_label || '',
    max_attendees: event.max_attendees ?? '',
    skill_levels: Array.isArray(event.skill_levels) && event.skill_levels.length ? event.skill_levels : ['all'],
    contact_email: event.contact_email || '',
    external_link: event.external_link || '',
    additional_notes: event.additional_notes || '',
    signup_instructions: event.signup_instructions || '',
    banner_image: event.banner_image || '',
    is_free: event.is_free !== false,
    cost_amount: event.cost_amount || 0,
    cost_description: event.cost_description || '',
  });
  const [saving, setSaving] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [status, setStatus] = useState(event.status || 'active');
  const isCanceled = status === 'canceled';
  // Inline PayPal upgrade flow — surfaced when the host tries to switch a
  // published event from free → paid and the backend responds with a
  // 402 `upgrade_fee_required`. The host pays the differential right
  // here without bouncing out to email support. See
  // `create_event_upgrade_order` / `capture_event_upgrade_order` in
  // backend/routes/events.py.
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null); // { differential, oldFee, newFee, message }
  const [upgradeBusy, setUpgradeBusy] = useState(false);

  useEffect(() => {
    // Hosts may hit the 402 upgrade flow. Admins never do (they're
    // exempt by design — manual corrections). Only fetch the PayPal
    // client config for hosts so we don't waste the request.
    if (!isAdmin) {
      api.get('/paypal/config').then(({ data }) => setPaypalConfig(data)).catch(() => {});
    }
  }, [isAdmin]);

  // Host-only coupon redeem (e.g. duplicated event still showing a fee
  // because the host hadn't yet redeemed MFMfirst6 at duplicate time).
  // Only relevant for hosts editing a draft with a non-zero posting fee.
  const showCouponBox =
    !isAdmin
    && event.payment_status === 'draft'
    && Number(event.posting_fee || 0) > 0
    && !event.free_post_pending;
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleSkill = (s) => setF(p => ({ ...p, skill_levels: p.skill_levels.includes(s) ? p.skill_levels.filter(x => x !== s) : [...p.skill_levels, s] }));

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    try {
      // Step 1: redeem the coupon (bumps user's `event_free_posts_remaining`).
      await api.post('/coupons/redeem-event', { code });
      // Step 2: apply one banked free post to *this* draft so the stored
      // posting_fee drops to $0 immediately. Without this the draft would
      // keep its original tier fee and the host would still be charged.
      await api.post(`/events/${event.event_id}/apply-free-post`);
      toast.success('Coupon applied — your posting fee has been waived for this event.');
      setCouponApplied(true);
      onSaved({ ...event, posting_fee: 0, free_post_pending: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const cancelEvent = async () => {    if (!window.confirm('Cancel this event? Saved & interested attendees will be notified by email/SMS. This cannot be undone.')) return;
    setCanceling(true);
    try {
      await api.post(`/events/${event.event_id}/cancel`);
      toast.success('Event canceled. Attendees notified.');
      setStatus('canceled');
      onSaved({ ...event, status: 'canceled' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCanceling(false);
    }
  };

  const buildPayload = () => ({
    title: f.title,
    event_type: f.event_type,
    description: f.description,
    event_date: f.event_date,
    start_time: f.start_time,
    end_time: f.end_time,
    timezone: f.timezone,
    is_recurring: !!f.is_recurring,
    recurring_frequency: f.is_recurring ? f.recurring_frequency : '',
    venue_name: f.venue_name,
    street_address: f.street_address,
    city: f.city,
    state: f.state,
    zip_code: f.zip_code,
    location_type: f.location_type,
    is_destination_event: !!f.is_destination_event,
    destination_label: f.destination_label,
    max_attendees: f.max_attendees === '' || f.max_attendees === null ? null : parseInt(f.max_attendees) || null,
    skill_levels: f.skill_levels.length ? f.skill_levels : ['all'],
    contact_email: f.contact_email,
    external_link: f.external_link,
    additional_notes: f.additional_notes,
    signup_instructions: f.signup_instructions,
    banner_image: f.banner_image,
    cost_description: f.cost_description,
    // Hosts + admins can now both flip free/paid and adjust the
    // attendee price. Previously gated to admins, which sent
    // support tickets like Rene's (Jun 14 2026 — "my event is
    // listed as free and there's a $20 fee, I can't change it").
    is_free: f.is_free,
    cost_amount: f.is_free ? 0 : Number(f.cost_amount) || 0,
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      await api.put(`/events/${event.event_id}`, payload);
      toast.success('Event updated');
      onSaved({ ...event, ...payload });
      onClose();
    } catch (err) {
      // The backend returns a structured 402 with `code:
      // upgrade_fee_required` when a host tries to switch a published
      // event from free to paid without settling the posting-fee
      // differential. Surface an inline PayPal payment prompt so the
      // host can pay the differential immediately instead of having to
      // email support.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.code === 'upgrade_fee_required') {
        setUpgradePrompt({
          differential: Number(detail.differential || 0),
          oldFee: Number(detail.old_fee || 0),
          newFee: Number(detail.new_fee || 0),
          message: detail.message || '',
        });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Called after the upgrade endpoint confirms the event is now paid
  // (either via PayPal capture or via the SFP / no-differential
  // short-circuit). Re-runs the PUT so the host's other edits in this
  // modal session (title, description, date, etc.) persist alongside
  // the new pricing.
  const finalizeUpgrade = async (newCostAmount) => {
    try {
      const payload = { ...buildPayload(), is_free: false, cost_amount: Number(newCostAmount) || 0 };
      await api.put(`/events/${event.event_id}`, payload);
      toast.success('Event upgraded to paid and saved.');
      onSaved({ ...event, ...payload });
      onClose();
    } catch (err) {
      // Pricing is already flipped server-side; flag the host so they
      // know the upgrade succeeded even if the follow-up save didn't.
      toast.error(
        err?.response?.data?.detail?.message
        || err?.response?.data?.detail
        || 'Pricing was upgraded, but saving the other edits failed. Reopen the event to retry.'
      );
      setUpgradePrompt(null);
      onSaved({ ...event, is_free: false, cost_amount: Number(newCostAmount) || 0 });
      onClose();
    }
  };


  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-event-edit-overlay"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="admin-event-edit-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-3xl z-10">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{isAdmin ? 'Edit event (admin)' : 'Edit event'}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100" data-testid="admin-event-edit-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* BASICS */}
          <Section title="Basics">
            <div>
              <Label className="text-xs uppercase tracking-wider">Title</Label>
              <Input value={f.title} onChange={e => u('title', e.target.value)} className="mt-1" data-testid="admin-event-edit-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Event type</Label>
              <select
                value={f.event_type}
                onChange={e => u('event_type', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]"
                data-testid="admin-event-edit-event-type"
              >
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Description</Label>
              <Textarea
                value={f.description}
                onChange={e => u('description', e.target.value)}
                rows={4}
                className="mt-1"
                data-testid="admin-event-edit-description"
              />
            </div>
          </Section>

          {/* BANNER IMAGE */}
          <Section title="Banner image">
            <div data-testid="admin-event-edit-banner-section">
              <ImageUpload
                value={f.banner_image}
                onChange={v => u('banner_image', v)}
                label="Upload Banner"
                shape="rect"
              />
              <p className="text-[11px] text-slate-400 mt-2 text-center leading-relaxed" data-testid="admin-event-edit-banner-size-hint">
                Best size: <strong>1600 × 600 px</strong> (wide banner, 16:6 ratio). Keep important text centered — grid thumbnails crop top &amp; bottom.
                <br />Click the image to replace, or tap the × to remove. JPG/PNG, max 10MB.
              </p>
            </div>
          </Section>

          {/* DATE & TIME */}
          <Section title="Date &amp; time">
            <div>
              <Label className="text-xs uppercase tracking-wider">Event date</Label>
              <div className="mt-1"><DatePicker value={f.event_date} onChange={v => u('event_date', v)} testId="admin-event-edit-date" minToday={false} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Start time</Label>
                <div className="mt-1"><TimePicker12h value={f.start_time} onChange={v => u('start_time', v)} testId="admin-event-edit-start-time" /></div>
                <p className="text-[11px] text-slate-400 mt-1">Saved in venue local time</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">End time</Label>
                <div className="mt-1"><TimePicker12h value={f.end_time} onChange={v => u('end_time', v)} testId="admin-event-edit-end-time" /></div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Time zone</Label>
              <select
                value={f.timezone}
                onChange={e => u('timezone', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]"
                data-testid="admin-event-edit-timezone"
              >
                {EVENT_TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={f.is_recurring}
                  onChange={e => u('is_recurring', e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#56dfb4] focus:ring-[#56dfb4]"
                  data-testid="admin-event-edit-is-recurring"
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-800">Recurring event</span>
                  <span className="block text-xs text-slate-500">Repeats on a schedule (weekly, monthly, etc.)</span>
                </span>
              </label>
              {f.is_recurring && (
                <select
                  value={f.recurring_frequency}
                  onChange={e => u('recurring_frequency', e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]"
                  data-testid="admin-event-edit-recurring-frequency"
                >
                  {RECURRING_OPTIONS.filter(o => o.value !== '').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </div>
          </Section>

          {/* LOCATION */}
          <Section title="Location">
            <div>
              <Label className="text-xs uppercase tracking-wider">Location type</Label>
              <select
                value={f.location_type}
                onChange={e => u('location_type', e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]"
                data-testid="admin-event-edit-location-type"
              >
                {LOCATION_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Venue name</Label>
              <Input value={f.venue_name} onChange={e => u('venue_name', e.target.value)} className="mt-1" data-testid="admin-event-edit-venue" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Street address</Label>
              <Input value={f.street_address} onChange={e => u('street_address', e.target.value)} className="mt-1" data-testid="admin-event-edit-street" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs uppercase tracking-wider">City</Label>
                <Input value={f.city} onChange={e => u('city', e.target.value)} className="mt-1" data-testid="admin-event-edit-city" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">State</Label>
                <select
                  value={f.state}
                  onChange={e => u('state', e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]"
                  data-testid="admin-event-edit-state"
                >
                  <option value="">--</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Zip</Label>
                <Input
                  value={f.zip_code}
                  onChange={e => u('zip_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
                  maxLength={5}
                  className="mt-1"
                  data-testid="admin-event-edit-zip"
                />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={f.is_destination_event}
                  onChange={e => u('is_destination_event', e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#56c6df] focus:ring-[#56c6df]"
                  data-testid="admin-event-edit-is-destination"
                />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-800">🌍 Destination event</span>
                  <span className="block text-xs text-slate-500">Bypasses distance filters — open to players nationwide.</span>
                </span>
              </label>
              {f.is_destination_event && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Destination label</Label>
                  <Input
                    value={f.destination_label}
                    onChange={e => u('destination_label', e.target.value)}
                    placeholder="e.g. Carnival Caribbean Cruise"
                    className="mt-1"
                    data-testid="admin-event-edit-destination-label"
                  />
                </div>
              )}
            </div>
          </Section>

          {/* DETAILS */}
          <Section title="Details">
            <div>
              <Label className="text-xs uppercase tracking-wider">Max attendees</Label>
              <Input
                type="number"
                min="0"
                value={f.max_attendees ?? ''}
                onChange={e => u('max_attendees', e.target.value)}
                placeholder="Leave blank for unlimited"
                className="mt-1"
                data-testid="admin-event-edit-max-attendees"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Skill levels</Label>
              <div className="flex flex-wrap gap-2">
                {SKILL_OPTIONS.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSkill(s)}
                    className={`px-3 py-1.5 rounded-full text-sm border capitalize transition-all ${f.skill_levels.includes(s) ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`admin-event-edit-skill-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Contact email</Label>
              <Input
                type="email"
                value={f.contact_email}
                onChange={e => u('contact_email', e.target.value)}
                className="mt-1"
                data-testid="admin-event-edit-contact-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">External signup / RSVP link</Label>
              <Input
                value={f.external_link}
                onChange={e => u('external_link', e.target.value)}
                placeholder="https://..."
                className="mt-1"
                data-testid="admin-event-edit-external-link"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Sign-up instructions</Label>
              <Textarea
                value={f.signup_instructions}
                onChange={e => u('signup_instructions', e.target.value)}
                rows={3}
                placeholder="How attendees should sign up or what to bring"
                className="mt-1"
                data-testid="admin-event-edit-signup-instructions"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Additional notes</Label>
              <Textarea
                value={f.additional_notes}
                onChange={e => u('additional_notes', e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="admin-event-edit-additional-notes"
              />
            </div>
          </Section>

          {/* PRICING — visible to both hosts and admins. Hosts can flip
              free/paid and adjust the attendee cost on their own events.
              The platform's posting fee was locked at draft creation, so
              this only changes what attendees pay. */}
          <Section title={isAdmin ? 'Pricing (admin view)' : 'Pricing'}>
              <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="admin-event-edit-is-free"
                    checked={f.is_free}
                    onChange={e => u('is_free', e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-[#56dfb4] focus:ring-[#56dfb4]"
                    data-testid="admin-event-edit-is-free"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-slate-800">Free event (no cost to attend)</span>
                    <span className="block text-xs text-slate-500">Uncheck to mark as paid and enter a price below.</span>
                  </span>
                </label>
                {!f.is_free && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Attendee cost (USD)</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium pointer-events-none">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={f.cost_amount}
                          onChange={e => u('cost_amount', e.target.value)}
                          className="pl-7"
                          data-testid="admin-event-edit-cost-amount"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Cost description</Label>
                      <Input
                        value={f.cost_description}
                        onChange={e => u('cost_description', e.target.value)}
                        placeholder="e.g. Covers tea & snacks"
                        className="mt-1"
                        data-testid="admin-event-edit-cost-desc"
                      />
                    </div>
                  </div>
                )}
                {f.is_free && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Cost description (optional)</Label>
                    <Input
                      value={f.cost_description}
                      onChange={e => u('cost_description', e.target.value)}
                      placeholder="e.g. Bring a snack to share"
                      className="mt-1"
                      data-testid="admin-event-edit-cost-desc-free"
                    />
                  </div>
                )}
                {!isAdmin && (
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Toggling free/paid here only changes what attendees pay. Your platform posting fee was locked
                    when you first created the event and isn&apos;t affected.
                  </p>
                )}
              </div>
            </Section>
          {/* HOST-ONLY: confirmation banner — a banked free post has been
              auto-applied to this draft (e.g. from the duplicate flow or
              the modal coupon redeem). Reassures the host they won't be
              charged at publish time. */}
          {!isAdmin && event.free_post_pending && event.payment_status === 'draft' && (
            <div
              className="rounded-2xl border border-[#56dfb4]/40 bg-[#56dfb4]/10 p-4 flex items-start gap-3"
              data-testid="host-edit-free-post-pending-banner"
            >
              <Sparkles className="w-5 h-5 text-[#1f8a6a] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#1f8a6a]">Free post applied to this event</p>
                <p className="text-xs text-[#1f8a6a]/80 mt-0.5">
                  Your posting fee is waived. When you publish, one of your banked free posts will be used — no payment needed.
                </p>
              </div>
            </div>
          )}
          {/* HOST-ONLY: redeem a promo code on a duplicated draft that
              still carries a posting fee. Hidden once a free post is
              already pending or once the host successfully redeems. */}
          {showCouponBox && !couponApplied && (
            <Section title="Have a promo code?">
              <div
                className="p-4 rounded-2xl border border-dashed border-[#b456df]/30 bg-[#b456df]/5"
                data-testid="host-edit-coupon-section"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-[#b456df]" />
                  <p className="text-sm text-slate-600">
                    Enter a code to waive this event&apos;s posting fee.
                  </p>
                </div>
                <div className="flex gap-2 items-stretch">
                  <Input
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    className="rounded-xl uppercase"
                    disabled={couponLoading}
                    data-testid="host-edit-coupon-input"
                  />
                  <Button
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-6"
                    data-testid="host-edit-coupon-apply-btn"
                  >
                    {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              </div>
            </Section>
          )}
          {/* DANGER ZONE — cancel event */}
          <Section title="Danger zone">
            {isCanceled ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="admin-event-edit-canceled-banner">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-700">This event has been canceled</p>
                  <p className="text-xs text-red-600/80 mt-0.5">Saved &amp; interested attendees have already been notified.</p>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-red-200 bg-red-50/50 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-700">Cancel this event</p>
                    <p className="text-xs text-red-600/80 mt-0.5">
                      The event will be hidden from the public list and everyone who saved or
                      expressed interest will be emailed and texted. This cannot be undone.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={cancelEvent}
                  disabled={canceling}
                  variant="outline"
                  className="rounded-full border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700 w-full sm:w-auto"
                  data-testid="admin-event-edit-cancel-event-btn"
                >
                  {canceling ? 'Canceling…' : 'Cancel event'}
                </Button>
              </div>
            )}
          </Section>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100 sticky bottom-0 bg-white rounded-b-3xl">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="admin-event-edit-cancel-btn">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || !f.title.trim() || !f.event_date}
            className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]"
            data-testid="admin-event-edit-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>

        {/* Inline PayPal upgrade prompt — host changing published event
            from free to paid must settle the posting-fee differential
            before the change takes effect. */}
        {upgradePrompt && (
          <div
            className="fixed inset-0 z-[60] bg-slate-900/70 flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="event-upgrade-paypal-overlay"
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="event-upgrade-paypal-modal"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <CreditCard className="w-5 h-5 text-[#56c6df]" />
                  Switch to paid event
                </h3>
                <button
                  onClick={() => { setUpgradePrompt(null); }}
                  className="p-1 hover:bg-slate-100 rounded-full"
                  aria-label="Close"
                  data-testid="event-upgrade-paypal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                Paid events carry a higher platform posting fee than free events. To flip this published event to paid,
                you owe the differential below. Once payment completes your edits — including the new attendee price —
                are saved automatically.
              </p>
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 mb-4 text-sm">
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Original posting fee</span><span className="font-medium">${upgradePrompt.oldFee.toFixed(2)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-slate-500">Paid-event posting fee</span><span className="font-medium">${upgradePrompt.newFee.toFixed(2)}</span></div>
                <div className="flex justify-between pt-2 mt-2 border-t border-slate-200">
                  <span className="font-semibold text-slate-700">You pay today</span>
                  <span className="font-bold text-[#df5681]" data-testid="event-upgrade-differential">${upgradePrompt.differential.toFixed(2)}</span>
                </div>
              </div>
              {upgradeBusy && (
                <div className="flex items-center justify-center py-3 text-sm text-slate-500" data-testid="event-upgrade-busy">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Finalizing…
                </div>
              )}
              {!upgradeBusy && paypalConfig && (
                <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: 'USD', intent: 'capture' }}>
                  <PayPalButtons
                    style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                    forceReRender={[upgradePrompt.differential, event.event_id]}
                    createOrder={async () => {
                      try {
                        const { data } = await api.post(`/events/${event.event_id}/upgrade/create-order`, {
                          cost_amount: Number(f.cost_amount) || 0,
                        });
                        // SFP / zero-differential short-circuit — backend
                        // already flipped the event; no PayPal capture
                        // needed. Persist the rest of the edits.
                        if (data?.applied) {
                          setUpgradeBusy(true);
                          await finalizeUpgrade(Number(f.cost_amount) || 0);
                          return null;
                        }
                        return data.order_id;
                      } catch (err) {
                        // Distinguish 502 (PayPal/payment provider
                        // outage) from a generic 4xx so the host knows
                        // whether to retry vs reach out for support.
                        if (err?.response?.status === 502) {
                          toast.error('Payment provider is unreachable. Please retry in a moment.');
                        } else {
                          const detail = err?.response?.data?.detail;
                          toast.error(typeof detail === 'string' ? detail : 'Could not start payment. Try again.');
                        }
                        throw err;
                      }
                    }}
                    onApprove={async (data) => {
                      setUpgradeBusy(true);
                      try {
                        const { data: cap } = await api.post(`/events/${event.event_id}/upgrade/capture`, {
                          order_id: data.orderID,
                        });
                        if (cap?.applied) {
                          await finalizeUpgrade(cap.cost_amount ?? (Number(f.cost_amount) || 0));
                        } else {
                          toast.error('Payment status: ' + (cap?.status || 'unknown'));
                          setUpgradeBusy(false);
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Capture failed. Please retry.');
                        setUpgradeBusy(false);
                      }
                    }}
                    onError={(err) => {
                      console.error('PayPal upgrade error:', err);
                      toast.error('PayPal error. Please try again.');
                    }}
                    onCancel={() => {
                      toast.info('Payment canceled. Your event is still free.');
                    }}
                  />
                </PayPalScriptProvider>
              )}
              {!upgradeBusy && !paypalConfig && (
                <p className="text-xs text-slate-400 text-center">Loading PayPal…</p>
              )}
              <p className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed">
                Cancel to keep this event free. Closing this dialog without paying will leave the event unchanged.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
